/**
 * MarthaOrb — Audio-reactive brand orb visualization.
 *
 * A pulsing orb that responds to voice state and audio levels.
 * Colors are sourced from the active brand palette (CSS vars set by
 * AppearanceProvider): --c-coral-500/600/700 for Martha, Archie equivalents
 * when brand=archie. The orb auto-themes with the rest of the app.
 */

import { useMemo } from 'react';

export type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking';

interface MarthaOrbProps {
  state: OrbState;
  /** Audio level 0-1, drives pulse intensity */
  audioLevel?: number;
  /** Size in px (default 48) */
  size?: number;
}

const STATE_STYLES: Record<OrbState, { color: string; shadow: string; animation: string; border?: string }> = {
  idle: {
    color: 'rgba(255, 255, 255, 0.85)',
    shadow: '0 0 12px rgba(255, 255, 255, 0.3)',
    animation: 'martha-orb-idle 4s ease-in-out infinite',
    border: '2px solid rgba(255, 255, 255, 0.5)',
  },
  listening: {
    color: 'rgb(var(--c-coral-500))',
    shadow: '0 0 30px rgb(var(--c-coral-500) / 0.5)',
    animation: 'martha-orb-listen 1.5s ease-in-out infinite',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
  thinking: {
    color: 'rgb(var(--c-coral-600))',
    shadow: '0 0 25px rgb(var(--c-coral-600) / 0.4)',
    animation: 'martha-orb-think 3s ease-in-out infinite',
    border: '2px solid rgba(255, 255, 255, 0.4)',
  },
  speaking: {
    color: 'rgb(var(--c-coral-700))',
    shadow: '0 0 40px rgb(var(--c-coral-700) / 0.6)',
    animation: 'martha-orb-speak 0.8s ease-in-out infinite',
    border: '2px solid rgba(255, 255, 255, 0.5)',
  },
};

export function MarthaOrb({ state, audioLevel = 0, size = 48 }: MarthaOrbProps) {
  const { color, shadow, animation, border } = STATE_STYLES[state];

  // Scale transform based on audio level (subtle 1.0–1.15 range)
  const scale = 1 + audioLevel * 0.15;

  const style = useMemo(
    () => ({
      width: size,
      height: size,
      borderRadius: '50%',
      background: state === 'speaking'
        ? `radial-gradient(circle, rgb(var(--c-coral-500)) 0%, rgb(var(--c-coral-700)) 100%)`
        : color,
      boxShadow: shadow,
      border,
      animation,
      transform: `scale(${scale})`,
      transition: 'transform 0.1s ease-out, background 0.3s ease',
    }),
    [state, color, shadow, border, animation, scale, size],
  );

  return (
    <>
      <div className="martha-orb" style={style} />
      <style>{orbKeyframes}</style>
    </>
  );
}

const orbKeyframes = `
@keyframes martha-orb-idle {
  0%, 100% { opacity: 0.85; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
}
@keyframes martha-orb-listen {
  0%, 100% { transform: scale(1); box-shadow: 0 0 20px rgb(var(--c-coral-500) / 0.3); }
  50% { transform: scale(1.06); box-shadow: 0 0 35px rgb(var(--c-coral-500) / 0.6); }
}
@keyframes martha-orb-think {
  0%, 100% { opacity: 0.7; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
}
@keyframes martha-orb-speak {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}
`;
